import React from 'react';
import { Link, graphql } from "gatsby";
import Seo from "../components/seo";
import investment from '../images/investments-feature.webp';
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { BLOCKS, MARKS } from '@contentful/rich-text-types';
import { Layout } from "../components/Layouts";
import { WebinarForm, SingleWebinarHeroSection, Cta, TrustpilotSimple, WebinarSpotlight } from "../components";
import { FacebookShareButton, LinkedinShareButton, TwitterShareButton } from 'react-share';
import { FaFacebook, FaLinkedin, FaTwitter } from 'react-icons/fa';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import howItWorks from '../images/how-it-works.webp';
import { ExternalLink } from '../components/Links';

const options = {
  renderNode: {
    [BLOCKS.HEADING_2]: (node, children) => <h2 style={{ marginTop: '40px' }}>{children}</h2>,
    [BLOCKS.HEADING_3]: (node, children) => <h3 style={{ marginTop: '40px' }}>{children}</h3>,
    [BLOCKS.HEADING_4]: (node, children) => <h4 style={{ marginTop: '20px' }}>{children}</h4>,
  },
  renderMark: {
    [MARKS.BOLD]: text => <strong>{text}</strong>,
  },
};

function getOrdinalSuffix(date) {
  const dayOfMonth = date.getDate();
  if (dayOfMonth > 3 && dayOfMonth < 21) return 'th';
  switch (dayOfMonth % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
}

export default function Webinar({ data }) {
  if (!data || !data.contentfulWebinars) {
    console.error('Webinar data is not available');
    return <p>Webinar data is not available</p>;
  }

  const webinar = data.contentfulWebinars;
  const speakerHostBio = webinar.speakerHostBio ? JSON.parse(webinar.speakerHostBio.raw) : null;
  const speakerHostBioComponents = speakerHostBio ? documentToReactComponents(speakerHostBio, options) : null;
  const speakerHostHeadshotImage = webinar.speakerHostHeadshot ? getImage(webinar.speakerHostHeadshot) : null;
  const speaker1HeadshotImage = webinar.speaker1Headshot ? getImage(webinar.speaker1Headshot) : null;
  const speaker2HeadshotImage = webinar.speaker2Headshot ? getImage(webinar.speaker2Headshot) : null;
  const speaker3HeadshotImage = webinar.speaker3Headshot ? getImage(webinar.speaker3Headshot) : null;
  const speaker4HeadshotImage = webinar.speaker4Headshot ? getImage(webinar.speaker4Headshot) : null;
  const extraSpeakers = [
    { name: webinar.speaker1Name, jobTitle: webinar.speaker1JobTitle, companyName: webinar.speaker1CompanyName },
    { name: webinar.speaker2Name, jobTitle: webinar.speaker2JobTitle, companyName: webinar.speaker2CompanyName },
    { name: webinar.speaker3Name, jobTitle: webinar.speaker3JobTitle, companyName: webinar.speaker3CompanyName },
    { name: webinar.speaker4Name, jobTitle: webinar.speaker4JobTitle, companyName: webinar.speaker4CompanyName },
  ].filter(speaker => speaker.name && speaker.jobTitle && speaker.companyName);

  const webinarContentJson = webinar.aboutTheWebinar ? JSON.parse(webinar.aboutTheWebinar.raw) : null;
  const webinarContentComponents = webinarContentJson ? documentToReactComponents(webinarContentJson, options) : null;

  const isUpcoming = new Date(webinar.webinarDate) > new Date();
  const keyPoints = [
    webinar.keyPoint1,
    webinar.keyPoint2,
    webinar.keyPoint3,
    webinar.keyPoint4,
    webinar.keyPoint5,
    // Only add keyPoint6 if it exists
    webinar.keyPoint6 ? webinar.keyPoint6 : null
  ].filter(Boolean); 

  const url = typeof window !== 'undefined' ? window.location.href : '';
  const webinarDateObject = new Date(webinar.webinarDate);

  const bgImageData = data.backgroundImage.childImageSharp.gatsbyImageData;
  const description = isUpcoming
  ? `Join us for our webinar | ${webinar.webinarTitle}`
  : `Watch our webinar | ${webinar.webinarTitle}`;

const canonicalUrl = webinar.webinarCanonical || `https://holbornassets.sa/webinars/${webinar.slug}`;    
    return (
      <Layout>
          <Seo
            title={`${webinar.webinarTitle} | Holborn Assets Saudi Arabia`}
            description={description}
            canonical={canonicalUrl}
            />
        {/* Render SingleWebinarHeroSection for the specific webinar */}
        <SingleWebinarHeroSection
            title={webinar.webinarTitle}
            webinarDateObject={webinarDateObject}
            getOrdinalSuffix={getOrdinalSuffix}
            isUpcoming={isUpcoming}
            ytLink={webinar.youtubeUrl}
            bgImageData={bgImageData}
            />

            {/* About The Webinar */}
            <div className="section w-full pt-16 pb-20 bg-light-blue">
                <div className="grid grid-cols-12 mx-auto px-4 gap-4">
                    <div
                        className="col-span-full lg:col-start-2 lg:col-end-7 lg:col-span-5 lg:px-0">
                        <div className="holborn-presents">
                            <h2 className="text-2xl font-bold text-holborn-blue">Holborn Webinars Presents</h2>
                            <p>Welcome to the Holborn Assets webinar series. As a leading global financial
                                services company, we're committed to empowering our clients with the knowledge
                                and tools to navigate the complex, offshore financial landscape. Our webinars
                                offer valuable insights from our financial consultants and industry experts,
                                covering a wide array of topics.</p>
                            <p>Whether you're a seasoned investor, just starting on your financial journey
                                or thinking of making the move overseas, our webinars are geared to educate,
                                inspire, and provide actionable strategies that align with your unique financial
                                goals. Explore the details below to learn more about our upcoming webinar, and
                                join us at the event by registering.</p>
                        </div>
                        <div className="about-the-webinar pt-10">
                            <h2 className="text-2xl font-bold text-holborn-blue">About the Webinar</h2>
                            {webinarContentComponents}
                        </div>
                        <div className="key-points pt-10">
                            <h2 className="text-2xl font-bold text-holborn-blue">Key Points</h2>
                            <ul className="list-disc list-inside leading-loose">
                                {keyPoints.map((point, index) => (
                                    <li key={index}>{point}</li>
                                ))}
                            </ul>
                        </div>
                        <div
                            className="webinar-social-media mt-10 flex">
                            <span className="pr-4 pt-10">Share:</span>
                            <div className="mt-10">
                                <FacebookShareButton url={url}>
                                    <FaFacebook size={24} className="mr-4"/>
                                </FacebookShareButton>
                                <TwitterShareButton url={url}>
                                    <FaTwitter size={24} className="mr-4"/>
                                </TwitterShareButton>
                                <LinkedinShareButton url={url}>
                                    <FaLinkedin size={24} className="mx-10"/>
                                </LinkedinShareButton>
                            </div>
                        </div>
                    </div>
                    <div id='form' className="col-span-full lg:col-start-8 lg:col-end-12 lg:col-span-4 px-30 mt-10 lg:mt-0">
                        <WebinarForm 
                        isUpcoming={webinar.isUpcoming} 
                        webinar={webinar}
                        />
                    </div>
                </div>
            </div>

            {/* Speakers */}
                <div className="section w-full pt-16 pb-10 bg-holborn-blue text-white">
                <div className="grid grid-cols-12 mx-auto px-4 gap-4">
                    {extraSpeakers.length > 0 ? (
                        <div className="col-span-full lg:col-start-2 lg:col-end-5 lg:col-span-4 lg:px-0">
                            <h2 className="pb-10">Meet your webinar host</h2>
                            <div className="content-wrapper">
                                <div className="speaker-headshot-wrapper">
                                    <div className="speaker-headshot pb-4" style={{ width: '350px', height: 'auto' }}>
                                        {speakerHostHeadshotImage && <GatsbyImage image={speakerHostHeadshotImage} alt="Speaker Headshot" style={{ width: '100%', height: '100%' }} />}
                                    </div>
                                </div>
                                <div className="speaker-content-wrapper">
                                    <h4 className="font-bold py-2">{webinar.speakerHostName}</h4>
                                    <div className="flex">
                                        <p>{`${webinar.speakerHostJobTitle}, ${webinar.speakerHostCompanyName}`}</p>
                                    </div>
                                    <p>{speakerHostBioComponents}</p>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="col-span-full lg:col-start-2 lg:col-end-10 lg:col-span-7 lg:px-0">
                            <h2 className="pb-10">Meet your webinar host</h2>
                            <div className="content-wrapper grid lg:flex lg:gap-10">
                                <div className="speaker-headshot-wrapper">
                                    <div className="speaker-headshot pb-4" style={{ width: '350px', height: 'auto' }}>
                                        {speakerHostHeadshotImage && <GatsbyImage image={speakerHostHeadshotImage} alt="Speaker Headshot" style={{ width: '100%', height: '100%' }} />}
                                    </div>
                                </div>
                                <div className="speaker-content-wrapper px-4 md:px-10 md:pt-6">
                                    <h4 className="font-bold py-2">{webinar.speakerHostName}</h4>
                                    <div className="flex">
                                        <p>{`${webinar.speakerHostJobTitle}, ${webinar.speakerHostCompanyName}`}</p>
                                    </div>
                                    <p>{speakerHostBioComponents}</p>
                                </div>
                            </div>
                        </div>
                    )}
                    {/* Other Speakers */}
                        {   (webinar?.speaker1Name && webinar?.speaker1JobTitle && webinar?.speaker1CompanyName) ||
                                (webinar?.speaker2Name && webinar?.speaker2JobTitle && webinar?.speaker2CompanyName) ||
                                (webinar?.speaker3Name && webinar?.speaker3JobTitle && webinar?.speaker3CompanyName) ||
                                (webinar?.speaker4Name && webinar?.speaker4JobTitle && webinar?.speaker4CompanyName) ? (
                            <div className="col-span-full lg:col-start-7 lg:col-end-12 lg:col-span-5 lg:px-0">
                                <h2 className="pb-10">Other Speakers</h2>
                                <div className="flex flex-wrap">
                                {webinar?.speaker1Name && webinar?.speaker1JobTitle && webinar?.speaker1CompanyName && (
                                    <div className="w-full md:w-1/2 mb-6">
                                        <div className="speaker-headshot" style={{ width: '200px', height: 'auto' }}>
                                            {speaker1HeadshotImage && <GatsbyImage image={speaker1HeadshotImage} alt="Speaker Headshot" style={{ width: '100%', height: '100%' }} />}
                                        </div>
                                        <h5 className="font-bold py-2">{webinar.speaker1Name}</h5>
                                        <div className="flex">
                                            <p>{`${webinar.speaker1JobTitle}, ${webinar.speaker1CompanyName}`}</p>
                                        </div>
                                    </div>
                                )}
                                {webinar.speaker2Name && webinar.speaker2JobTitle && webinar.speaker2CompanyName && (
                                    <div className="w-full md:w-1/2 mb-6">
                                        <div className="speaker-headshot" style={{ width: '200px', height: 'auto' }}>
                                        {speaker2HeadshotImage && <GatsbyImage image={speaker2HeadshotImage} alt="Speaker Headshot" style={{ width: '100%', height: '100%' }} />}
                                        </div>
                                        <h5 className="font-bold py-2">{webinar.speaker2Name}</h5>
                                        <div className="flex">
                                            <p>{`${webinar.speaker2JobTitle}, ${webinar.speaker2CompanyName}`}</p>
                                        </div>
                                    </div>
                                )}
                                {webinar.speaker3Name && webinar.speaker3JobTitle && webinar.speaker3CompanyName && (
                                    <div className="w-full md:w-1/2 mb-6">
                                        <div className="speaker-headshot" style={{ width: '200px', height: 'auto' }}>
                                            {speaker3HeadshotImage && <GatsbyImage image={speaker3HeadshotImage} alt="Speaker Headshot" style={{ width: '100%', height: '100%' }} />}
                                        </div>
                                        <h5 className="font-bold py-2">{webinar.speaker3Name}</h5>
                                        <div className="flex">
                                            <p>{`${webinar.speaker3JobTitle}, ${webinar.speaker3CompanyName}`}</p>
                                        </div>
                                    </div>
                                )}
                                {webinar?.speaker4Name && webinar?.speaker4JobTitle && webinar?.speaker4CompanyName && (
                                    <div className="w-full md:w-1/2 mb-6">
                                        <div className="speaker-headshot" style={{ width: '200px', height: 'auto' }}>
                                        {speaker4HeadshotImage && (
                                            <GatsbyImage image={speaker4HeadshotImage} alt="Speaker 4 Headshot" />
                                            )}
                                        </div>
                                        {webinar.speaker4Name && webinar.speaker4JobTitle && webinar.speaker4CompanyName && (
                                            <>
                                                <h5 className="font-bold py-2">{webinar.speaker4Name}</h5>
                                                <div className="flex">
                                                    <p>{`${webinar.speaker4JobTitle}, ${webinar.speaker4CompanyName}`}</p>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                )}
                                </div>
                            </div>
                        ) : null}
                    </div>
                </div>
            {/* Webinar Spotlights */}
             
            {/* <WebinarSpotlight /> */}
            {/* How it works */}
            <div className="section how-it-works w-full py-16 bg-light-blue">
                <div className="grid grid-cols-12 w-full relative pt-14">
                    <div className="col-span-12 lg:col-start-2 lg:col-span-10">
                        <h2 className="pb-10 text-center pb-12 ">How it works</h2>
                    </div>
                    <div className="col-span-12 lg:col-start-3 lg:col-span-10">
                        <div className="grid grid-cols-12 w-full relative">
                            <div className='col-span-12 xl:col-span-6 px-6'>
                                <div className='flex items-start mb-8'>
                                    <span style={{ height: '50px', display: 'flex', alignItems: 'center', marginTop: '7px' }} className='bg-holborn-blue flex text-white p-6 mr-10 text-lg'>1</span>
                                    <p>Signing up to a Holborn webinar easy and free. Simply complete the  <Link className='font-bold' to={'#form'}>registration form</Link> above and we’ll take care of the rest. After you’ve registered, you'll receive a confirmation email from Zoom containing your webinar joining link and all relevant details. As the webinar date approaches, look out for reminder emails to make sure you don’t miss out!</p>
                                </div>
                                <div className='flex items-start mb-8'>
                                    <span style={{ height: '50px', display: 'flex', alignItems: 'center', marginTop: '7px' }} className='bg-holborn-blue flex text-white p-6 mr-10 text-lg'>2</span>
                                    <p>On the day of the webinar, click the unique link provided in your confirmation or reminder emails to access the live session. You'll be taken straight to the webinar, where you’ll be able to watch the presenters, and engage through the Q&A or chat features. Attendee’s microphones and cameras are disabled for webinars, so you can just sit back and enjoy the presentation, wherever you are.</p>
                                </div>
                                <div className='flex items-start mb-8'>
                                    <span style={{ height: '50px', display: 'flex', alignItems: 'center', marginTop: '7px' }} className='bg-holborn-blue flex text-white p-6 mr-10 text-lg'>3</span>
                                    <p>Holborn webinars are always recorded, so if you can’t make it or want to watch again, you don’t need to worry. Once the webinar has finished, we upload the recording to our <ExternalLink to="https://www.youtube.com/@HolbornAssetsLtd">YouTube channel</ExternalLink> and send the link out to everyone that registered. You can also revisit this page and watch the recording on our website too.</p>
                                </div>
                                <p>If you have any questions for the webinar host before or after the webinar, simply complete the <Link className='font-bold' to={'#form'}>form</Link> above.</p>
                            </div>
                            <div className='col-span-12 lg:col-span-4 px-10 text-right'>
                                <img className='hidden xl:block' src={howItWorks} alt='How it works' />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <TrustpilotSimple />
            <Cta />
        </Layout>
    );
}

export const pageQuery = graphql`
query WebinarBySlug($slug: String!) {
  backgroundImage: file(relativePath: {eq: "webinar-hub-header-image.webp"}) {
    childImageSharp {
      gatsbyImageData(layout: FULL_WIDTH)
    }
  }
  contentfulWebinars(slug: {eq: $slug}) {
    webinarCanonical
    slug
    id
    webinarTitle
    youtubeUrl
    aboutTheWebinar {
      raw
    }
    keyPoint1
    keyPoint2
    keyPoint3
    keyPoint4
    keyPoint5
    webinarDate
    speakerHostName
    speakerHostJobTitle
    speakerHostCompanyName
    speakerHostBio {
      raw
    }
    speakerHostHeadshot {
      gatsbyImageData(layout: FULL_WIDTH)
    }
    speaker1Headshot {
      gatsbyImageData(layout: FULL_WIDTH)
    }
    speaker2Headshot {
      gatsbyImageData(layout: FULL_WIDTH)
    }
    speaker3Headshot {
      gatsbyImageData(layout: FULL_WIDTH)
    }
    speaker4Headshot {
      gatsbyImageData(layout: FULL_WIDTH)
    }
    speaker1Name
    speaker1JobTitle
    speaker1CompanyName
    speaker2Name
    speaker2JobTitle
    speaker2CompanyName
    speaker3Name
    speaker3JobTitle
    speaker3CompanyName
    speaker4Name
    speaker4JobTitle
    speaker4CompanyName
    headerImage {
      gatsbyImageData(layout: FULL_WIDTH)
    }
  }
  allContentfulWebinars(sort: {webinarDate: DESC}, limit: 1) {
    edges {
      node {
        id
        slug
        webinarTitle
        aboutTheWebinar {
          raw
        }
        webinarDate
        webinarLength
        headerImage {
          gatsbyImageData(layout: FULL_WIDTH)
        }
      }
    }
  }
  blueBackgroundImage: file(relativePath: {eq: "hero-bg-image.webp"}) {
    childImageSharp {
      gatsbyImageData(layout: FULL_WIDTH)
    }
  }
}

`;